/**
 * Canonical public paths for the AI Search Analytics product.
 *
 * Keep route construction here so a future public-path rename does not require
 * touching every navigation, filter, report, and redirect call site. This file
 * is plain ESM so it can be shared by both Next's config and application code.
 */
export const ANALYTICS_BASE_PATH = '/analytics'
export const LEGACY_ANALYTICS_BASE_PATH = '/traffic'

const analyticsPath = (segment) => `${ANALYTICS_BASE_PATH}/${segment}`

export const ANALYTICS_ROUTES = Object.freeze({
  root: ANALYTICS_BASE_PATH,
  report: analyticsPath('report'),
  internalReport: analyticsPath('report/internal'),
  // The category-opportunity report's no-app base path - a sibling of
  // report/internal under /report, matching /report/[slug]/category for the
  // app-scoped view. See parseAnalyticsReportPathname's RESERVED_REPORT_SLUGS.
  categoryReport: analyticsPath('report/category'),
  crawlers: analyticsPath('crawlers'),
  sessions: analyticsPath('sessions'),
  pages: analyticsPath('pages'),
  agents: analyticsPath('agents'),
  placementReview: analyticsPath('placement-review'),
  billing: analyticsPath('billing'),
  settings: analyticsPath('settings'),
  citations: analyticsPath('citations'),
  aeo: analyticsPath('prompts'),
  docs: analyticsPath('docs'),
})

export function isAnalyticsPath(pathname) {
  return pathname === ANALYTICS_BASE_PATH || pathname.startsWith(`${ANALYTICS_BASE_PATH}/`)
}

export function isLegacyAnalyticsPath(pathname) {
  return (
    pathname === LEGACY_ANALYTICS_BASE_PATH || pathname.startsWith(`${LEGACY_ANALYTICS_BASE_PATH}/`)
  )
}

const REPORT_VIEW_NAMES = new Set(['search', 'activity', 'training', 'category', 'prompts'])

export function parseAnalyticsReportPathname(pathname) {
  const prefix = `${ANALYTICS_ROUTES.report}/`
  if (!pathname.startsWith(prefix)) return { slug: null, view: null }

  const [firstSegment = null, secondSegment = null] = pathname.slice(prefix.length).split('/')

  // /report/category is the no-app category overview - 'category' here IS
  // the view (like /report/[slug]/category), not an app slug.
  if (firstSegment === 'category') return { slug: null, view: 'category' }
  // 'internal' isn't a slug either - it's the separate query-param-based
  // internal report, which has no slug-scoped sub-views of its own.
  if (firstSegment === 'internal') return { slug: null, view: null }

  const view = REPORT_VIEW_NAMES.has(secondSegment) ? secondSegment : null
  return { slug: firstSegment || null, view }
}
